import * as angular from 'angular';
import * as _ from 'lodash';
import * as CP from './connectionPoints.constants';
import * as Actions from './connectionPoints.actions';

import { IConnectionPointService } from '../../services';
import { Dispatcher, GetStore } from '../Store';
import { ConnectionPoint, ConnectionPointTestResult } from '../../models';

export interface IConnectionPointActionsService {
    getAll: (force?: boolean) => Dispatcher<ConnectionPoint>;
    updateOne: (cp: ConnectionPoint, isDatabaseCreationRequested?: boolean) => Dispatcher<ConnectionPoint>;
    deleteOne: (guid: string) => Dispatcher<number>;
    duplicate: (guid: string) => Dispatcher<ConnectionPoint>;
    testAll: (force?: boolean) => Dispatcher<ConnectionPointTestResult[]>;
    test: (guid: string, force?: boolean) => Dispatcher<boolean>;
}

export class ConnectionPointActionsService implements IConnectionPointActionsService {

    static serviceId = 'connectionPointActionsService';
    static $inject = ['$q', 'connectionPointService'];

    constructor(
        private _$q: angular.IQService,
        private _connectionPointService: IConnectionPointService
    ) {
    }

    getAll(force?: boolean): Dispatcher<ConnectionPoint> {
        return (dispatch: Function, getState: GetStore) => {
            const { connectionPoints } = getState();
            if (force || !connectionPoints.isLoading && !connectionPoints.isLoaded) {
                dispatch({ type: CP.CONNECTION_POINTS_LOAD });
                return this._connectionPointService
                    .getAll(force)
                    .then(data => {
                        const result = data.map(d => d.connectionPoint);
                        dispatch({ type: CP.CONNECTION_POINTS_LOAD_SUCCESS, payload: result });
                        return result;
                    })
                    .catch((reason) => {
                        dispatch({ type: CP.CONNECTION_POINTS_LOAD_ERROR, payload: reason });
                        return reason;
                    });
            } else {
                return this._$q.when(_.values(connectionPoints.dictionary));
            }
        };
    }
    
    updateOne(cp: ConnectionPoint, isDatabaseCreationRequested?: boolean): Dispatcher<ConnectionPoint> {
        return (dispatch: Function, getStore: GetStore) => {
            dispatch({ type: CP.CONNECTION_POINT_UPDATE });
            return this._connectionPointService
                .updateOne(cp, isDatabaseCreationRequested)
                .then(data => {
                    const updated = _.find(data.map(d => d.connectionPoint), d => d.guid === cp.guid);
                    dispatch({ type: CP.CONNECTION_POINT_UPDATE_SUCCESS, payload: updated });
                    dispatch(this.test(updated.guid, true));
                    return updated;
                })
                .catch(reason => {
                    dispatch({ type: CP.CONNECTION_POINT_UPDATE_ERROR, payload: reason });
                    return reason;
                });
        };
    }

    deleteOne(guid: string): Dispatcher<number> {
        return (dispatch: Function, getStore: GetStore) => {
            dispatch(Actions.deleteConnectionPoint(guid));
            return this._connectionPointService
                .deleteOne(guid)
                .then(data => {
                    dispatch(Actions.deleteSuccess(guid));
                    return data;
                }).catch(reason => {
                    dispatch(Actions.deleteError(reason));
                    return reason;
                });
        };
    }

    duplicate(guid: string): Dispatcher<ConnectionPoint> {
        return (dispatch: Function, getStore: GetStore) => {
            dispatch(Actions.duplicate(guid));
            return this._connectionPointService
                .duplicate(guid)
                .then(data => {
                    const result = data.connectionPoint;
                    dispatch(Actions.duplicateSuccess(guid));
                    return dispatch(this.getAll(true));
                }).catch(reason => {
                    dispatch(Actions.duplicateError(reason));
                    return reason;
                });
        };
    }

    testAll(force?: boolean): Dispatcher<ConnectionPointTestResult[]> {
        return (dispatch: Function, getState: GetStore) => {
            const { connectionPointsStatuses } = getState();
            if (force || !connectionPointsStatuses.isLoading && !connectionPointsStatuses.isLoaded) {
                dispatch({ type: CP.CONNECTION_POINTS_STATUSES_LOAD });
                return this._connectionPointService
                    .testAll()
                    .then(data => {
                        dispatch({ type: CP.CONNECTION_POINTS_STATUSES_LOAD_SUCCESS, payload: data });
                        return data;
                    })
                    .catch(reason => {
                        dispatch({ type: CP.CONNECTION_POINTS_STATUSES_LOAD_ERROR, payload: reason });
                        return reason;
                    });
            } else {
                return this._$q.when(_.values<ConnectionPointTestResult>(connectionPointsStatuses.dictionary));
            }
        };
    }

    test(guid: string, force?: boolean): Dispatcher<boolean> {
        return (dispatch: Function, getState: GetStore) => {
            const { connectionPointsStatuses } = getState();
            if (force || !connectionPointsStatuses.isLoading && !connectionPointsStatuses.isLoaded) {
                dispatch({ type: CP.CONNECTION_POINT_STATUS_LOAD, payload: {guid: guid} });
                return this._connectionPointService
                    .test(guid)
                    .then(data => {
                        dispatch({ type: CP.CONNECTION_POINT_STATUS_LOAD_SUCCESS, payload: { available: data.isAvailable, guid, test: data } });
                        return data;
                    })
                    .catch((reason) => {
                        dispatch({ type: CP.CONNECTION_POINT_STATUS_LOAD_ERROR, payload: reason });
                        return reason;
                    });
            } else {
                return this._$q.when(_.values<ConnectionPointTestResult>(connectionPointsStatuses.dictionary));
            }
        };
    }
}